import React, { useState } from 'react';

import { mixpanelObject } from 'src/utils/analytics';
import {
  clearRentalMixpanelData,
  triggerLearnMorePopUpMixpanelEvent
} from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { GenerateThumbnailObject } from 'src/utils/ConsumptionHelper/ConsumptionHelper';
import { ContentMetaData } from 'src/utils/ConsumptionHelper/types';
import {
  getActivePartnerSubscriptions,
  isUserSubscribedToPartnerCheck
} from 'src/utils/contentPartnerHelper';
import { getDeviceWidth } from 'src/utils/getDeviceWidth';
import { IMAGE_PATH_AND_NAME } from 'src/utils/ImageHelper/ImagePathHelper';
import { getImageURL } from 'src/utils/ImageHelper/ImageURLGenerator';
import {
  MixpanelRailTitle,
  MixpanelRailTypes,
  RailsMixpanelPayload
} from 'src/utils/railsMixpanelPayloadTypes';

import { useAppStore } from 'src/hooks/useAppStore';
import {
  MIXPANEL_ACTIVE_PARTNER_SUBSCRIPTION,
  MIXPANEL_AGGREGATOR_HOME_PAGE,
  MIXPANEL_AGGREGATOR_PARTNER_ID,
  MIXPANEL_AGGREGATOR_PARTNER_NAME,
  MIXPANEL_AGGREGATOR_USER_TYPE,
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_CAROUSAL_ID,
  MIXPANEL_CAROUSAL_NAME,
  MIXPANEL_CROSS,
  MIXPANEL_CTA,
  MIXPANEL_ELEMENT,
  MIXPANEL_HORIZONTAL_INDEX,
  MIXPANEL_MORE,
  MIXPANEL_NOT_SUBSCRIBED,
  MIXPANEL_PAGE_NAME,
  MIXPANEL_SOURCE,
  MIXPANEL_SUBSCRIBED,
  MIXPANEL_VERTICAL_INDEX
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  contentBannerClick,
  viewMoreSelectedEvent
} from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import Carousel from '../Carousel';
import TextMessage from '../consumptionPage/ConsumptionMetaData/TextMessage';
import DropDown from '../dropDown/DropDown';
import PlexLearnNowPopup from '../plexLearnNowPopup/PlexLearnNowPopup';
import ThumbnailComponent from '../thumbnail';
import { ThumbnailTypes } from '../thumbnail/types';
import { getBreakPointsConfig } from './railsHelper';
import * as customStyled from './RailsStyle';

interface RailsProps {
  showTitle: boolean;
  contents: any;
  tagName: keyof typeof IMAGE_PATH_AND_NAME;
  thumbnailType: ThumbnailTypes;
  title: string;
  moreLink: string;
  verticalIndex: number;
  isCollectionResolved?: boolean;
  dropdownOptions?: string[];
  changeIntervalHandler?: (index: number, item: string) => Promise<void>;
  updatedEpisodesRange?: string | undefined;
  showTitleWithinThumbnail?: boolean;
}

const Rails: React.FC<RailsProps> = ({
  contents,
  moreLink,
  showTitle,
  tagName,
  thumbnailType,
  title,
  verticalIndex,
  isCollectionResolved,
  dropdownOptions,
  changeIntervalHandler,
  updatedEpisodesRange,
  showTitleWithinThumbnail
}) => {
  const { translations, usersPreferenceData, subscriptionData } = useAppStore();
  const consumptionTranslations = translations?.consumption;

  const titleDataTestid = title
    ? `${title.replaceAll(' ', '-').toLowerCase()}-`
    : '';
  const translationData = translations?.plex;
  const isPlexTray = tagName === 'plexWidget';

  const [triggerLearnMorePopUp, setTriggerLearnMorePopUp] = useState(false);

  const plexInfoClickMixpanelEvent = () => {
    mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_CTA);
    mixpanelObject.setProperty(MIXPANEL_ELEMENT, 'Info');
    clearRentalMixpanelData(mixpanelObject);
    contentBannerClick(true);
  };

  const triggerLearnMorePopUpHandler = (isOkBtn = false) => {
    setTriggerLearnMorePopUp((prevState) => !prevState);
    let element;
    if (triggerLearnMorePopUp) {
      element = isOkBtn
        ? translationData.How_Consent_Consent_Button
        : MIXPANEL_CROSS;
    }
    triggerLearnMorePopUpMixpanelEvent(triggerLearnMorePopUp, element);
  };

  const informationHandler = () => {
    plexInfoClickMixpanelEvent();
    setTriggerLearnMorePopUp(true);
  };

  const renderThumbnails = () => {
    return contents.map((content: ContentMetaData, index: number) => {
      if (!Object.keys(content).length) return <></>;
      const generateThumbnailInstance: GenerateThumbnailObject =
        GenerateThumbnailObject.getThumbnailInstance(
          thumbnailType === ThumbnailTypes.episode
            ? ThumbnailTypes.horizontal
            : thumbnailType,
          tagName,
          {
            content,
            hoverOut: true,
            displayTitleOnImage: false
          }
        );
      const generateThumbnailObject = generateThumbnailInstance.generate();

      const imageURL = getImageURL({
        tagName: generateThumbnailObject.tagName,
        deviceWidth: getDeviceWidth() || 0,
        item: {
          id: content.id,
          title: content.title,
          assetType: content.assetType,
          image: content.image || content?.image_thumbnail
        }
      });
      const cardDetails = {
        contentId: content.id,
        title: title || '',
        horizontalIndex: index + 1,
        verticalIndex: verticalIndex + 1,
        assetSubtype: content.assetSubType,
        originalTitle: content.originalTitle
      };

      /**
       * cardPropsGenerator({
        data: details,
        onActionClicked: onActionClicked,
        railTags: railTags!,
        breakPointsConfig,
        isInWatchlist:
          checkIfContentAvailableInWatchlist(userWatchlist, details) || false,
        isContinueWatchingRail: isContinueWatchingRail || false,
        translationText: translationText
      })
       */
      return (
        //Bellybanner ?
        <ThumbnailComponent
          key={generateThumbnailObject.contentId}
          {...generateThumbnailObject}
          imageUrl={imageURL}
          cardDetails={cardDetails}
          dataTestId={`${titleDataTestid}${index + 1}`}
          showTitleWithinThumbnail={showTitleWithinThumbnail}
        />
        // : cards
      );
    });
  };

  const generateMixpanelPayload = (): RailsMixpanelPayload => {
    return {
      trayDetails: {
        allCollectionsResolved: isCollectionResolved as boolean,
        index: verticalIndex,
        isEpisodeConsumption: false, // replace with thumbnailType
        isPendingResolved: true,
        isRecommendedColumn: false,
        pendingDataAvailable: false,
        title: MixpanelRailTitle.OTHER,
        tabName: '',
        talamoosModelName: '',
        type: MixpanelRailTypes.SIMILAR,
        carouselTitle: title
      },
      trayData: {
        content: contents.map((content: any) => ({
          title: content.title,
          value: content.title
        })),
        partnerDetails: {
          content_partner_id: usersPreferenceData?.partner
            ?.partner_id as number,
          content_partner_name: usersPreferenceData?.partner
            ?.partner_name as string
        }
      },
      activeSubscriptions: subscriptionData?.currentActiveSubscriptions
    };
  };

  const viewMoreMixpanelEvent = () => {
    // mixpanel events starts
    mixpanelObject[MIXPANEL_ELEMENT] = MIXPANEL_MORE;
    mixpanelObject[MIXPANEL_VERTICAL_INDEX] = verticalIndex || 'N/A';
    mixpanelObject[MIXPANEL_HORIZONTAL_INDEX] = 'N/A';
    mixpanelObject[MIXPANEL_CAROUSAL_ID] = contents?.[0]?.id || 'N/A';
    mixpanelObject[MIXPANEL_CAROUSAL_NAME] = title || 'N/A';

    const isUserSubscribedToPartner = isUserSubscribedToPartnerCheck({
      subscriptions: subscriptionData.currentActiveSubscriptions
    });
    const activePartnerSubscriptions = getActivePartnerSubscriptions() || 'N/A';
    mixpanelObject[MIXPANEL_AGGREGATOR_PARTNER_NAME] =
      (usersPreferenceData?.partner?.partner_name as string) || 'N/A';
    mixpanelObject[MIXPANEL_AGGREGATOR_PARTNER_ID] =
      (usersPreferenceData?.partner?.partner_id as number) || 'N/A';
    mixpanelObject[MIXPANEL_AGGREGATOR_USER_TYPE] = isUserSubscribedToPartner
      ? MIXPANEL_SUBSCRIBED
      : MIXPANEL_NOT_SUBSCRIBED;
    mixpanelObject[MIXPANEL_SOURCE] = MIXPANEL_AGGREGATOR_HOME_PAGE;
    mixpanelObject[MIXPANEL_PAGE_NAME] = MIXPANEL_AGGREGATOR_HOME_PAGE;
    mixpanelObject[MIXPANEL_ACTIVE_PARTNER_SUBSCRIPTION] =
      activePartnerSubscriptions;
    viewMoreSelectedEvent();
    // mixpanel events ends
  };

  const moreLinkNavigationHandler = () => {
    window.location.href = moreLink;
    viewMoreMixpanelEvent();
  };

  return (
    <React.Fragment>
      {contents?.length ? (
        <div
          css={customStyled.railsContainer()}
          key={verticalIndex}
          data-testid={`${titleDataTestid}rail-container`}>
          <div css={customStyled.railsHeaderStyle}>
            {showTitle ? (
              <div css={customStyled.railsTitleContainer}>
                <h3
                  data-testid={`${titleDataTestid}-rail`}
                  css={customStyled.railsTitle}>
                  <TextMessage
                    message={title}
                    link={moreLink}
                    styles={customStyled.railsTitle}
                  />
                  {isPlexTray ? (
                    <div
                      css={customStyled.informationStyle}
                      onClick={informationHandler}
                    />
                  ) : null}
                </h3>
              </div>
            ) : (
              <>
                {dropdownOptions && changeIntervalHandler ? (
                  <DropDown
                    intervalData={dropdownOptions}
                    changeEventHandler={changeIntervalHandler}
                    defaultSelected={updatedEpisodesRange}
                  />
                ) : null}
              </>
            )}
            {/**TODO: added div, subject to test in multiple devices.*/}
            {moreLink && (
              <div
                css={customStyled.moreLinkContainer}
                data-testid={`${titleDataTestid}-more-link`}>
                <TextMessage
                  message={consumptionTranslations?.MORE}
                  link={moreLink}
                  styles={customStyled.moreLinkStyle}
                />
              </div>
            )}
          </div>

          <Carousel
            breakPointsConfig={getBreakPointsConfig(thumbnailType)}
            mixpanelPayload={generateMixpanelPayload()}>
            {renderThumbnails()}
          </Carousel>
        </div>
      ) : null}
      {triggerLearnMorePopUp && (
        <PlexLearnNowPopup
          show={triggerLearnMorePopUp}
          closeHandler={triggerLearnMorePopUpHandler}
        />
      )}
    </React.Fragment>
  );
};

export default React.memo(Rails);
